import { useMemo } from 'react';

import { useApiClient, usePermissions } from '@hetivo/hetivo-lib-front-utils';
import { MODULE_AREA } from '@hetivo/hetivo-lib-types';
import type { SearchIssuesCriteria } from '@hetivo/hetivo-lib-types';
import { useQuery } from '@tanstack/react-query';

import { useTenantModules } from '&/hooks/use-tenant-modules';
import { useMasterContext } from '&/master';

import { createIssueApi, issueKeys } from '../api';
import { ISSUES_LIST_PERMISSION } from '../paths';

/**
 * The number of Open issues for the selected scope, for the sidebar badge (SWOOP-255, FR-014). It calls
 * the same stats endpoint as the page under the same key (scope only, no search/type), so the badge and
 * the page's Open KPI always agree (SC-004). Enabled only when the tenant owns Maintenance and the user
 * has `issues:list`; otherwise it never runs and returns `undefined` (no badge).
 */
export function useIssuesOpenCount(): number | undefined {
  const client = useApiClient('admin-auth');
  const { hasPermission } = usePermissions();
  const { hasModule } = useTenantModules();
  const { siteId, territoryId } = useMasterContext();

  const enabled = hasModule(MODULE_AREA.Maintenance) && hasPermission(ISSUES_LIST_PERMISSION);
  const criteria = useMemo<SearchIssuesCriteria>(
    () => ({ locationId: siteId ?? undefined, territoryId: territoryId ?? undefined }),
    [siteId, territoryId],
  );

  const { data } = useQuery({
    enabled,
    gcTime: 5 * 60_000,
    queryFn: () => createIssueApi(client).stats(criteria),
    queryKey: issueKeys.stats(criteria),
    staleTime: 30_000,
  });

  return data?.kpis.open;
}
